// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useGuestRideActions } from '../hooks/useGuestRideActions';
import { useUser } from '@store';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, Button } from '@mui/material';
import { BiSolidError } from 'react-icons/bi';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

// GRAPHQL ---------------------------------------------------------------- //

const SOFT_DELETE_GUESTRIDE = gql`
  mutation SoftDeleteGuestride($id: bigint!, $updated_by: String) {
    update_guestrides_by_pk(pk_columns: { id: $id }, _set: { status: "deleted", updated_by: $updated_by }) {
      id
      status
    }
  }
`;

// TYPES ---------------------------------------------------------------- //

interface DeleteRideModalProps {
  open: boolean;
  onClose: () => void;
  ride: any;
}

// COMPONENT ---------------------------------------------------------------- //

const DeleteRideModal: React.FC<DeleteRideModalProps> = ({ open, onClose, ride }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { refetch } = useGuestRideActions();
  const user = useUser();

  const [softDeleteGuestride, { loading }] = useMutation(SOFT_DELETE_GUESTRIDE);

  const handleConfirm = async () => {
    try {
      await softDeleteGuestride({
        variables: {
          id: ride?.id,
          updated_by: user?.profile?.email || null,
        },
      });

      toast.success('Ride deleted');
      if (refetch) refetch();
      onClose();
    } catch (err) {
      console.error('Failed to delete ride:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: 'delete-ride' },
        extra: { rideId: ride?.id, status: ride?.status },
      });
      toast.error('Failed to delete ride');
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>Delete Ride</ModalHeader>

      <ModalContent>
        <div className={cls.blurb}>
          <BiSolidError className={cls.blurbIcon} />
          <Typography variant='body2' sx={{ textWrap: 'pretty', color: theme.palette.error.dark }}>
            Are you sure you want to delete the ride for <strong>{ride?.guest_name || 'this guest'}</strong>? It will be
            moved to the Deleted tab and can be reverted later.
          </Typography>
        </div>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          <strong>Pickup:</strong> {ride?.pickup_address || 'No address provided'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>Dropoff:</strong> {ride?.dropoff_address || 'No address provided'}
        </Typography>
      </ModalContent>

      <ModalFooter>
        <Button disabled={loading} variant='text' color='utility' onClick={onClose}>
          Go Back
        </Button>
        <Button variant='contained' color='error' onClick={handleConfirm} disabled={loading}>
          {loading ? 'Deleting...' : 'Delete Ride'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  blurb: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.error?.light};
    border-radius: 10px;
    margin-bottom: 12px;
    background-color: ${theme?.palette?.error?.light}20;
  `,
  blurbIcon: css`
    min-width: 20px;
    min-height: 20px;
    font-size: 20px;
    color: ${theme?.palette?.error?.main};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default DeleteRideModal;
